// ─────────────────────────────────────────────────────────────
// Analytics
// Extracted from App.js.
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { AIInsights } from "./AIInsights";
import { BehaviorAnalytics } from "./BehaviorAnalytics";
import { ContextAnalytics } from "./ContextAnalytics";
import { PerformanceAnalytics } from "./PerformanceAnalytics";
import { PsychologyAnalytics } from "./PsychologyAnalytics";
import { RiskDiscipline } from "./RiskDiscipline";
import { Card } from "../../components/ui/Card";
import { Pill } from "../../components/ui/Pill";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { MonthlyCalendar } from "../../features/analytics/MonthlyCalendar";
import { buildAnalytics, classifyTrade } from "../../lib/analytics";
import { useDetectors } from "../../hooks/useDetectors";
import { writeSnapshots } from "../../lib/detectorState";
import { useUserRules } from "../../hooks/useUserRules";
import { C } from "../../lib/colors";
import { fmtPct, fmt$, tradeAmount } from "../../lib/format";
import { DisciplineAnalytics } from "./DisciplineAnalytics";
import { MonthlyReview } from "./MonthlyReview";
import { useViewport } from "../../hooks/useViewport";
import { BehavioralCostHero } from "./BehavioralCostHero";
import { TraderIdentityCard } from "./TraderIdentityCard";

export function Analytics({ trades, accounts = [], currentAccountId, setCurrentAccountId, getDailyNote, saveDailyNote, profile, userId }) {
  const { isMobile } = useViewport();
  const [dateRange, setDateRange] = useState("all");
  const [tab, setTab] = useState("performance");
  // "gross" = broker-reported P&L per trade (matches statement, good for
  //           strategy/setup analysis since costs are a separate concern)
  // "net"   = pnl minus fees and commissions (matches account balance)
  const [pnlMode, setPnlMode] = useState("net");

  // Date filtering
  const filteredTrades = useMemo(() => {
    if (!dateRange || dateRange === "all") return trades;

    const now = new Date();
    let cutoff = new Date();

    if (dateRange === "30d") cutoff.setDate(now.getDate() - 30);
    else if (dateRange === "90d") cutoff.setDate(now.getDate() - 90);
    else if (dateRange === "6m") cutoff.setMonth(now.getMonth() - 6);

    return trades.filter(t => new Date(t.trade_date) >= cutoff);
  }, [trades, dateRange]);

  const accountTrades = currentAccountId 
    ? filteredTrades.filter(t => t.account_id === currentAccountId) 
    : filteredTrades;

  // Detector results — fetched server-side via the run-detectors Edge Function.
  // Used by:
  //   - The BehavioralCostHero at the top of the page (needs all 4 suites)
  //   - The Calendar tab to flag days that have trades in the Hour Cliff
  //     worst slot (needs behavior detectors only)
  //
  // We fetch suite:"all" once and slice out what each consumer needs. This
  // does NOT eliminate the duplicate fetches each suite-tab component makes
  // when its tab is active (BehaviorAnalytics, RiskDiscipline, etc. each
  // make their own useDetectors call) — that's a larger refactor for another
  // pass. But it does avoid making things worse: the parent makes one call
  // instead of one-per-consumer.
  //
  // We pass profile + accountRules because the Risk Discipline suite uses
  // them to compute cost (rule-aware violations like overtrading correlation,
  // size adherence, drawdown proximity). Without them, the Risk sub-suite
  // within "all" would use defaults and produce a different number than the
  // Risk Discipline tab itself — which would break trust the first time a
  // user notices "hero says $718, Risk tab says $726, why?". The other three
  // suites (Behavior, Psychology, Context) ignore these args, so passing
  // them is free for those suites.
  const { rules: accountRules = [] } = useUserRules(userId, currentAccountId);
  const { data: allDetectorResults, loading: detectorsLoading } = useDetectors({
    suite: "all",
    trades: accountTrades,
    profile,
    rules: accountRules,
    userId,
  });

  // Behavior subset for MonthlyCalendar. Matches the original shape it expects
  // (a { detectors: [...] } object) so no changes needed in that component.
  const detectorResults = allDetectorResults?.behavior;

  // ── Write snapshots whenever Monthly Review tab is active and detectors are loaded ──
  // This ensures detector_snapshots builds up history even if users don't visit
  // individual suite tabs. Fires at most once per tab open (allDetectorResults stable).
  useEffect(() => {
    if (tab !== "monthly" || !allDetectorResults || detectorsLoading || !userId || !currentAccountId) return;
    const allDets = [
      ...(allDetectorResults.behavior?.detectors  || []),
      ...(allDetectorResults.risk?.detectors      || []),
      ...(allDetectorResults.context?.detectors   || []),
      ...(allDetectorResults.psychology?.detectors|| []),
    ];
    if (allDets.length === 0) return;
    writeSnapshots({ userId, accountId: currentAccountId, results: allDets })
      .catch(e => console.warn("[Analytics] writeSnapshots failed:", e));
  }, [tab, allDetectorResults, detectorsLoading, userId, currentAccountId]);


  const analytics = useMemo(() => buildAnalytics(accountTrades), [accountTrades]);

  // Monthly Breakdown
  const monthlyData = useMemo(() => {
    const byMonth = {};
    accountTrades.forEach(t => {
      const monthKey = t.trade_date ? t.trade_date.slice(0, 7) : "Unknown";
      if (!byMonth[monthKey]) byMonth[monthKey] = { pnl: 0, trades: 0, wins: 0, losses: 0 };
      byMonth[monthKey].pnl += tradeAmount(t, pnlMode);
      byMonth[monthKey].trades++;
      const k = classifyTrade(t);
      if (k === "win") byMonth[monthKey].wins++;
      else if (k === "loss") byMonth[monthKey].losses++;
    });

    return Object.entries(byMonth)
      .map(([month, data]) => {
        const denom = data.wins + data.losses;
        return {
          month,
          pnl: data.pnl,
          trades: data.trades,
          // null when no decisive trades; consumers should render "—"
          winRate: denom > 0 ? Math.round((data.wins / denom) * 100) : null,
        };
      })
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [accountTrades, pnlMode]);

  const activeAccounts = accounts.filter(a => !a.is_blown && !a.is_passed);

  if (!analytics) {
    return (
      <div>
        {/* Always render account selector so user can switch accounts */}
        {activeAccounts.length > 0 && (
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>Viewing Account</label>
              <select
                value={currentAccountId || ""}
                onChange={e => setCurrentAccountId(e.target.value)}
                style={{ width: "100%", padding: 12, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
              >
                {activeAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} — {acc.account_type?.toUpperCase()} (${acc.starting_balance})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div style={{ textAlign: "center", padding: 80, color: C.muted }}>
          Log some trades to unlock analytics.
        </div>
      </div>
    );
  }

  // Psychology Stats

  return (
    <div>
      {/* ACCOUNT + DATE RANGE SELECTOR */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {activeAccounts.length > 0 && (
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>Viewing Account</label>
            <select 
              value={currentAccountId || ""} 
              onChange={e => setCurrentAccountId(e.target.value)}
              style={{ width: "100%", padding: 12, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
            >
              {activeAccounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.name} — {acc.account_type?.toUpperCase()} (${acc.starting_balance})
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>Date Range</label>
          <select 
            value={dateRange} 
            onChange={e => setDateRange(e.target.value)}
            style={{ width: "100%", padding: 12, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
          >
            <option value="all">All Time</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="6m">Last 6 Months</option>
          </select>
        </div>

        <div style={{ flex: 1, minWidth: 220 }}>
          <label
            style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}
            title="Net = after fees and commissions (matches account balance). Gross = broker's raw P&L (matches statement, best for strategy analysis)."
          >
            P&amp;L View <span style={{ opacity: 0.5 }}>ⓘ</span>
          </label>
          <div style={{ display: "flex", background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, padding: 4, gap: 4 }}>
            {[
              ["net",   "Net",   "After fees & commissions"],
              ["gross", "Gross", "Broker-reported P&L"],
            ].map(([val, label, tip]) => (
              <button
                key={val}
                onClick={() => setPnlMode(val)}
                title={tip}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  background: pnlMode === val ? C.blue : "transparent",
                  color: pnlMode === val ? "#fff" : C.muted,
                  transition: "all 0.15s ease",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>Analytics Dashboard</h2>
      <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>
        {analytics.total} trades • {dateRange === "all" ? "All Time" : dateRange}
      </p>

      {/* ── KPI STRIP — always visible above tab nav ──────────────────────────
          Previously lived in the "Overview" tab. Promoted here so the 6 key
          numbers are visible regardless of which tab you're on.              */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
        gap: 8,
        marginBottom: 20,
      }}>
        {[
          [pnlMode === "net" ? "Total Net P&L" : "Total Gross P&L",
           fmt$(pnlMode === "net" ? analytics.netPnl : analytics.totalPnl),
           (pnlMode === "net" ? analytics.netPnl : analytics.totalPnl) >= 0 ? C.green : C.red],
          ["Win Rate",      fmtPct(analytics.winRate),              C.blue],
          ["Profit Factor", analytics.profitFactor.toFixed(2),      C.purple],
          ["Avg Win",       fmt$(analytics.avgWin),                 C.green],
          ["Avg Loss",      `-$${analytics.avgLoss.toFixed(2)}`,    C.red],
          ["Total Trades",  analytics.total,                        C.text],
        ].map(([l, v, c]) => (
          <div key={l} style={{
            padding: "10px 12px",
            background: "#1a1d2e",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: c, fontFamily: "monospace" }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Behavioral Cost Hero — page-level rollup of estimated monthly cost
          across all 4 detector suites. Sits between the financial KPI strip
          (above) and the tab nav (below) so it's visible on every tab. */}
      <BehavioralCostHero
        allDetectorResults={allDetectorResults}
        loading={detectorsLoading}
        tradeCount={accountTrades.length}
      />

      {/* Trader Identity Card — synthesizes all analytics into a single
          identity statement. Requires 10+ trades; invisible below that. */}
      <TraderIdentityCard
        analytics={analytics}
        allDetectorResults={allDetectorResults}
        trades={accountTrades}
        pnlMode={pnlMode}
      />

      {/* Tab Navigation
          Mobile (<768px): collapses to a <select> dropdown.
          Desktop: horizontal button bar with overflow-scroll.
          11 tabs → 7 tabs after Pass A cleanup. */}
      {isMobile ? (
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>View</label>
          <select
            value={tab}
            onChange={e => setTab(e.target.value)}
            style={{
              width: "100%",
              padding: 12,
              background: "#0b0d19",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.text,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <option value="performance">Performance</option>
            <option value="calendar">Calendar</option>
            <option value="behavior">Behavior</option>
            <option value="risk">Risk Discipline</option>
            <option value="psychology">Psychology</option>
            <option value="context">Context</option>
            <option value="setups">By Setup</option>
            <option value="insights">AI Insights</option>
            <option value="discipline">Discipline</option>
            <option value="monthly">Monthly Review</option>
          </select>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 4, background: "#0b0d19", border: `1px solid ${C.border}`, borderRadius: 10, padding: 4, marginBottom: 24, overflowX: "auto" }}>
          {[
            ["performance", "Performance"],
            ["calendar",    "Calendar"],
            ["behavior",    "Behavior"],
            ["risk",        "Risk Discipline"],
            ["psychology",  "Psychology"],
            ["context",     "Context"],
            ["setups",      "By Setup"],
            ["insights",    "AI Insights"],
            ["discipline",  "Discipline"],
            ["monthly",     "Monthly Review"],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{
                flex: 1, minWidth: 80, padding: "10px", borderRadius: 8, border: "none",
                background: tab === id ? C.panel : "transparent",
                color: tab === id ? C.text : C.muted,
                fontWeight: tab === id ? 700 : 400,
              }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* PSYCHOLOGY TAB — rebuilt as detector tab (Phase 8 Pass C) */}
      {tab === "psychology" && (
        <PsychologyAnalytics
          trades={accountTrades}
          userId={userId}
          accountId={currentAccountId}
          pnlMode={pnlMode}
        />
      )}

      {/* PERFORMANCE — equity, drawdown, rolling WR, distribution, monthly */}
      {tab === "performance" && (
        <PerformanceAnalytics
          trades={accountTrades}
          analytics={analytics}
          monthlyData={monthlyData}
          pnlMode={pnlMode}
        />
      )}

      {/* CALENDAR TAB — MonthlyCalendar promoted from Heatmaps container.
          The time-of-day bar, conviction scatter, and emotional radar that
          previously lived here are removed — they duplicated Behavior/Psychology. */}
      {tab === "calendar" && (
        <MonthlyCalendar
          trades={accountTrades}
          getDailyNote={getDailyNote}
          saveDailyNote={saveDailyNote}
          currentAccountId={currentAccountId}
          pnlMode={pnlMode}
          detectorResults={detectorResults}
        />
      )}

      {/* CONTEXT — news events, exit behavior, market regime, setup edge */}
      {tab === "context" && (
        <ContextAnalytics
          trades={accountTrades}
          userId={userId}
          accountId={currentAccountId}
        />
      )}

      {/* BY SETUP */}
      {tab === "setups" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Untagged trades prompt ── */}
          {(() => {
            const untagged = accountTrades.filter(t => !t.setup_type && !t.entry_signal).length;
            const total    = accountTrades.length;
            const pct      = total > 0 ? Math.round((untagged / total) * 100) : 0;
            if (pct < 20) return null; // most trades are tagged, no need to prompt
            return (
              <div style={{
                padding: "14px 18px",
                background: C.yellow + "0d",
                border: `1px solid ${C.yellow}40`,
                borderLeft: `3px solid ${C.yellow}`,
                borderRadius: 10,
                display: "flex",
                gap: 14,
                alignItems: "flex-start",
              }}>
                <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>🏷️</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.yellow, marginBottom: 4 }}>
                    {untagged} of {total} trades ({pct}%) have no setup tag
                  </div>
                  <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, marginBottom: 8 }}>
                    Setup analytics only show patterns with enough tagged trades to compare. Right now most of your data is grouped as "Unknown."
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
                    <strong style={{ color: C.text }}>Manual trades:</strong> Set the Setup field in Stage 1 when logging a trade.{" "}
                    <strong style={{ color: C.text }}>CSV imports:</strong> Open any trade in the Review tab and set the Setup Type field — or bulk-edit trades with the same pattern.
                  </div>
                </div>
              </div>
            );
          })()}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            {analytics.setupData.length > 0 && analytics.setupData
              .sort((a, b) => b.winRate - a.winRate)
              .slice(0, 4)
              .map((d, i) => (
                <Card key={i} style={{ padding: 16, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: C.muted }}>{d.setup}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: d.winRate >= 65 ? C.green : C.yellow, margin: "8px 0" }}>
                    {d.winRate}%
                  </div>
                  <div style={{ fontSize: 12, color: C.sub }}>{d.total} trades • {fmt$(d.pnl)}</div>
                </Card>
              ))}
          </div>

          <Card>
            <SectionTitle>Setup Performance Breakdown</SectionTitle>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                    <th style={{ padding: "12px 10px", textAlign: "left", color: C.muted }}>Setup</th>
                    <th style={{ padding: "12px 10px", textAlign: "center", color: C.muted }}>Win Rate</th>
                    <th style={{ padding: "12px 10px", textAlign: "center", color: C.muted }}>Expectancy</th>
                    <th style={{ padding: "12px 10px", textAlign: "center", color: C.muted }}>Avg R-Multiple</th>
                    <th style={{ padding: "12px 10px", textAlign: "center", color: C.muted }}>Total P&L</th>
                    <th style={{ padding: "12px 10px", textAlign: "center", color: C.muted }}>Avg Confidence</th>
                    <th style={{ padding: "12px 10px", textAlign: "center", color: C.muted }}>Avg Impulsiveness</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.setupData
                    .sort((a, b) => b.winRate - a.winRate)
                    .map((d, i) => {
                      const setupTrades = trades.filter(t => (t.setup_type || t.entry_signal) === d.setup);
                      const avgConfidence = setupTrades.reduce((s, t) => s + (t.conviction_level || 5), 0) / (setupTrades.length || 1);
                      const avgImpulsiveness = setupTrades.reduce((s, t) => s + (t.impulsiveness || 5), 0) / (setupTrades.length || 1);
                      const expectancy = d.total > 0 ? d.pnl / d.total : 0;

                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}20` }}>
                          <td style={{ padding: "12px 10px", fontWeight: 600 }}>{d.setup}</td>
                          <td style={{ padding: "12px 10px", textAlign: "center", color: d.winRate >= 65 ? C.green : C.yellow, fontWeight: 700 }}>
                            {d.winRate}%
                          </td>
                          <td style={{ padding: "12px 10px", textAlign: "center", fontFamily: "monospace" }}>{fmt$(expectancy)}</td>
                          <td style={{ padding: "12px 10px", textAlign: "center", fontFamily: "monospace" }}>{d.avgRMultiple ? d.avgRMultiple.toFixed(2) : "—"}</td>
                          <td style={{ padding: "12px 10px", textAlign: "center" }}><Pill value={d.pnl} /></td>
                          <td style={{ padding: "12px 10px", textAlign: "center", color: avgConfidence >= 7 ? C.green : C.yellow }}>{avgConfidence.toFixed(1)}</td>
                          <td style={{ padding: "12px 10px", textAlign: "center", color: avgImpulsiveness >= 7 ? C.red : C.yellow }}>{avgImpulsiveness.toFixed(1)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </Card>

          <Card glow={C.purple}>
            <SectionTitle>AI Setup Recommendation</SectionTitle>
            <p style={{ lineHeight: 1.6, color: C.text }}>
              Focus on setups with <strong>high confidence (≥8)</strong> and strong R-Multiples. 
              These show the best long-term edge and lowest impulsiveness.
            </p>
          </Card>
        </div>
      )}

      {/* AI INSIGHTS */}
            {/* IMPROVED AI INSIGHTS */}
      



      {/* HIGHLY DYNAMIC AI INSIGHTS */}
      {/* AI INSIGHTS — Claude synthesizes all detector outputs (Phase 8) */}
      {tab === "insights" && (
        <AIInsights
          trades={accountTrades}
          analytics={analytics}
          profile={profile}
          userId={userId}
          currentAccountId={currentAccountId}
        />
      )}







      {tab === "risk" && <RiskDiscipline trades={accountTrades} profile={profile} userId={userId} accountId={currentAccountId} />}

      {/* BEHAVIOR */}
      {tab === "behavior" && <BehaviorAnalytics trades={accountTrades} userId={userId} accountId={currentAccountId} />}

      {/* MONTHLY REVIEW — rebuilt as detector-delta narrative (Phase 8 Pass E) */}
      {tab === "monthly" && (
        <MonthlyReview
          userId={userId}
          accountId={currentAccountId}
          monthlyData={monthlyData}
        />
      )}




      {/* DISCIPLINE */}
      {tab === "discipline" && (
        <DisciplineAnalytics trades={accountTrades} setView={() => {}} accounts={accounts} currentAccountId={currentAccountId} setCurrentAccountId={setCurrentAccountId} pnlMode={pnlMode} />
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// CAN I TRADE — CHECKLIST WITH DETAILED REASONS
// ─────────────────────────────────────────────────────────────

